type KYCButtonAction = "backToDashboard" | "tryAgain" | "emailSupport";

export interface KYCFailSpecs {
  header: string;
  body: string | string[];
  primaryButton: {
    text: string;
    action: KYCButtonAction;
  };
  secondaryButton?: {
    text: string;
    action: KYCButtonAction;
  };
  state: "PENDING" | "MAX_ATTEMPTS_REACHED" | "MANUAL_REVIEW" | "RETRY_KYC";
}

export interface KYCApiResponse {
  data: {
    message?: string;
    PENDING?: any;
    edd?: {
      behavior: string;
      required_documents: string[];
      remaining_attempts: number;
      errors: string[];
    };
    kyc: string[];
  };
}

// EDD Document Type to readable text mapping
export const EDD_DOCUMENT_EXAMPLES: Record<string, string[]> = {
  GOVERNMENT_ISSUED_ID: [
    "U.S. Driver's License",
    "U.S. Passport or Passport Card",
  ],
  SSN_PROOF: ["Social Security Card", "W-2 Form"],
  ADDRESS_PROOF: [
    "Utility bill (gas, water, electric)",
    "Bank or credit card statement",
  ],
};

// EDD Behavior types
export const EDD_BEHAVIOR_TYPES = {
  MANUAL_REVIEW: "MANUAL_REVIEW",
  RETRY_KYC: "RETRY_KYC",
  REJECT_KYC: "REJECT_KYC",
} as const;

/**
 * Composable for handling KYC and EDD (Enhanced Due Diligence) logic
 */
export function useKYC() {
  const getEddDocumentsDescriptions = (documentTypes: string[]) => {
    return documentTypes
      .map((type) => {
        const docExamples = EDD_DOCUMENT_EXAMPLES[type];
        if (docExamples == null) {
          console.warn(`Unknown document type: ${type}`);
          return null;
        }
        return docExamples;
      })
      .filter(Boolean);
  };

  const getKYCFailureMessage = (data: KYCApiResponse["data"]): KYCFailSpecs => {
    const { message, edd } = data || {};

    const { behavior, remaining_attempts } = edd || {};

    const backButton = {
      text: "Back to dashboard",
      action: "backToDashboard" as KYCButtonAction,
    };

    const tryAgainButton = {
      text: "Try again",
      action: "tryAgain" as KYCButtonAction,
    };

    const emailSupportButton = {
      text: "Email Cloaked support",
      action: "emailSupport" as KYCButtonAction,
    };

    if (data?.PENDING && data.PENDING.length > 0) {
      return {
        header: "We're reviewing your information.",
        body: "We'll reach out to you with an update once our team has reviewed your documentation.",
        primaryButton: backButton,
        secondaryButton: emailSupportButton,
        state: "PENDING",
      };
    }

    if (
      (message && message.includes("Max KYC attempts reached.")) ||
      remaining_attempts === 0
    ) {
      return {
        header: "We weren't able to verify your identity.",
        body: "You're no longer eligible to try again.",
        primaryButton: backButton,
        secondaryButton: emailSupportButton,
        state: "MAX_ATTEMPTS_REACHED",
      };
    }

    if (behavior === EDD_BEHAVIOR_TYPES.MANUAL_REVIEW) {
      return {
        header: "We need a few more details to verify your identity.",
        body: "Please submit one of the following documents from each section to <u><a href='mailto:support@cloaked.com'>support@cloaked.com</a></u>. If you do not have these documents available, please contact Cloaked Support for further assistance",
        primaryButton: emailSupportButton,
        secondaryButton: { ...backButton, text: "Close" },
        state: "MANUAL_REVIEW",
      };
    }

    if (
      remaining_attempts &&
      remaining_attempts > 0 &&
      behavior === EDD_BEHAVIOR_TYPES.RETRY_KYC
    ) {
      const body =
        remaining_attempts > 1
          ? "Please try again."
          : [
              "Please review your information carefully — you have one final attempt remaining.",
              "<b>Need help?</b> Reach us anytime at <u><a href='mailto:support@cloaked.com'>support@cloaked.com</a></u>",
            ];
      return {
        header: "We couldn't verify your identity.",
        body,
        primaryButton: tryAgainButton,
        secondaryButton: emailSupportButton,
        state: "RETRY_KYC",
      };
    }

    return {
      header: "Unable to verify your identity",
      body: "Please try again.",
      primaryButton: tryAgainButton,
      secondaryButton: emailSupportButton,
      state: "RETRY_KYC",
    };
  };

  return {
    getEddDocumentsDescriptions,
    getKYCFailureMessage,
  };
}
